import { useEffect, useState } from "react";
import type { SessionUser, TeamMember } from "../../../shared/types";
import { Ico } from "../../components/shell";
import { AtlShell, AtlModalOverlay, AtlModalShell, XIcon } from "../../components/atelier";
import { useAuth } from "../../auth";
import { navigate } from "../../router";
import { api, ApiError } from "../../api";
import { alertDialog, confirmDialog } from "../../components/Dialog";

function initialsOf(name: string): string {
  const w = name.trim().split(/\s+/).filter(Boolean);
  if (w.length >= 2) return (w[0][0] + w[1][0]).toUpperCase();
  return name.trim().slice(0, 2).toUpperCase() || "??";
}

export default function Team() {
  const { user } = useAuth();
  const [users, setUsers] = useState<TeamMember[]>([]);
  const refreshAll = () => {
    api.listUsers().then((r) => setUsers(r.users)).catch(() => {});
  };
  useEffect(() => {
    refreshAll();
  }, []);
  if (!user) return null;

  const isSuper = user.role === "super_admin";
  return (
    <AtlShell active="Team" title="Team" sub={isSuper ? "MANAGE HOST & ADMIN ACCOUNTS" : "MANAGE HOST ACCOUNTS"}>
      <div style={{ padding: 30, display: "flex", flexDirection: "column", gap: 18, maxWidth: 860 }}>
        <TeamCard me={user} users={users} onChanged={refreshAll} />
      </div>
    </AtlShell>
  );
}

function TeamCard({ me, users, onChanged }: { me: SessionUser; users: TeamMember[]; onChanged: () => void }) {
  const { impersonate, setUser } = useAuth();
  const isSuper = me.role === "super_admin";
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ displayName: "", username: "", password: "", role: "host" as "host" | "admin" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<TeamMember | null>(null);

  // You can edit your own row; a super admin can edit anyone but the super
  // account is never editable by others (there's only one, and it's yourself).
  const canEdit = (u: TeamMember) => u.id === me.id || (isSuper ? u.role !== "super_admin" : u.role === "host");

  async function create() {
    if (busy) return;
    setBusy(true);
    setErr("");
    try {
      await api.createUser(form.username.trim(), form.password, form.displayName.trim() || form.username.trim(), form.role);
      setForm({ displayName: "", username: "", password: "", role: "host" });
      setOpen(false);
      onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Could not create account");
    } finally {
      setBusy(false);
    }
  }

  async function del(u: TeamMember) {
    const ok = await confirmDialog({
      title: `Delete ${u.displayName}?`,
      message: `${u.ownedCount > 0 ? `Their ${u.ownedCount} quiz${u.ownedCount === 1 ? "" : "zes"} will move to the recycle bin under you. ` : ""}Any shares are removed and they're signed out. This removes the account.`,
      tone: "danger",
      confirmLabel: "Delete account",
    });
    if (!ok) return;
    try {
      await api.deleteUser(u.id);
      onChanged();
    } catch (e) {
      alertDialog(e instanceof ApiError ? e.message : "Could not delete account");
    }
  }

  return (
    <div className="atl-card" style={{ padding: 24 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 18 }}>
        <div>
          <h3 className="atl-serif" style={{ fontSize: 19 }}>Team accounts</h3>
          <p style={{ fontSize: 13, color: "var(--atl-soft)", fontWeight: 500, marginTop: 2 }}>
            {isSuper ? "Provision host & admin logins for your trainers." : "Provision host logins for your trainers."}
          </p>
        </div>
        <button className="atl-btn atl-btn--primary" style={{ marginLeft: "auto" }} onClick={() => setOpen((o) => !o)}>
          <Ico name="plus" size={15} /> New account
        </button>
      </div>

      {open && (
        <div style={{ background: "var(--atl-surface-2)", border: "1px solid var(--atl-line)", borderRadius: 16, padding: 18, marginBottom: 16 }}>
          <div className="atl-respond" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div>
              <div className="atl-eyebrow" style={{ marginBottom: 8 }}>NAME</div>
              <input className="atl-tf" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} placeholder="Sam B." />
            </div>
            <div>
              <div className="atl-eyebrow" style={{ marginBottom: 8 }}>USERNAME</div>
              <input className="atl-tf" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value.replace(/\s/g, "") })} placeholder="sam" />
            </div>
            <div>
              <div className="atl-eyebrow" style={{ marginBottom: 8 }}>TEMPORARY PASSWORD</div>
              <input className="atl-tf" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 6 characters" />
            </div>
            {isSuper && (
              <div>
                <div className="atl-eyebrow" style={{ marginBottom: 8 }}>ROLE</div>
                <select className="atl-select" style={{ width: "100%" }} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as "host" | "admin" })}>
                  <option value="host">Host</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16 }}>
            <button className="atl-btn atl-btn--primary" disabled={busy || form.username.length < 3 || form.password.length < 6} onClick={create}>
              Create account
            </button>
            {err && (
              <span className="atl-chip" style={{ fontWeight: 600, borderColor: "#e2c5ca", color: "var(--atl-accent)", background: "#fbf2f3" }}>
                {err}
              </span>
            )}
          </div>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        {users.map((u) => (
          <div key={u.id} className="atl-row" style={{ padding: "11px 16px" }}>
            <span style={{ width: 38, height: 38, borderRadius: 11, background: "var(--atl-gold)", color: "#fff", display: "grid", placeItems: "center", fontWeight: 700, fontSize: 13, flex: "0 0 auto" }}>
              {initialsOf(u.displayName)}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14.5 }}>
                {u.displayName}
                {u.id === me.id && <span style={{ fontSize: 12, color: "var(--atl-faint)", fontWeight: 500 }}> · you</span>}
              </div>
              <div style={{ fontSize: 12.5, color: "var(--atl-faint)", fontWeight: 500 }}>@{u.username}</div>
            </div>
            <div className="atl-rowactions" style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <span
                className="atl-chip"
                style={{
                  borderColor: u.role !== "host" ? "#e2c5ca" : "var(--atl-line)",
                  color: u.role !== "host" ? "var(--atl-accent)" : "var(--atl-soft)",
                  background: u.role !== "host" ? "#fbf2f3" : "var(--atl-surface)",
                }}
              >
                {u.role === "super_admin" ? "Super admin" : u.role === "admin" ? "Admin" : "Host"}
              </span>
              {canEdit(u) && (
                <button
                  className="atl-btn atl-btn--ghost"
                  style={{ padding: "8px 13px", fontSize: 12.5 }}
                  onClick={() => setEditing(u)}
                >
                  Edit
                </button>
              )}
              {isSuper && u.id !== me.id && (
                <button
                  className="atl-btn atl-btn--ghost"
                  style={{ padding: "8px 13px", fontSize: 12.5 }}
                  onClick={async () => {
                    try {
                      await impersonate(u.id);
                      navigate("/");
                    } catch (e) {
                      alertDialog(e instanceof ApiError ? e.message : "Could not impersonate");
                    }
                  }}
                >
                  Impersonate
                </button>
              )}
              {u.id !== me.id && (
                <button className="atl-iconbtn atl-iconbtn--danger" title="Delete account" onClick={() => del(u)}>
                  <XIcon size={14} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <EditAccountModal
          target={editing}
          isSelf={editing.id === me.id}
          canChangeRole={isSuper && editing.id !== me.id && editing.role !== "super_admin"}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setEditing(null);
            if (updated.id === me.id) setUser(updated);
            onChanged();
          }}
        />
      )}
    </div>
  );
}

const USERNAME_RE = /^[a-z0-9._-]{3,32}$/i;

/** Edit an existing account: name, username, role (when allowed) and an optional
    password reset (blank = keep current). The server re-checks every rule. */
function EditAccountModal({
  target,
  isSelf,
  canChangeRole,
  onClose,
  onSaved,
}: {
  target: TeamMember;
  isSelf: boolean;
  canChangeRole: boolean;
  onClose: () => void;
  onSaved: (u: SessionUser) => void;
}) {
  const [displayName, setDisplayName] = useState(target.displayName);
  const [username, setUsername] = useState(target.username);
  const [role, setRole] = useState<"host" | "admin">(target.role === "admin" ? "admin" : "host");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const uname = username.trim().toLowerCase();
  const unameValid = USERNAME_RE.test(uname);
  const passValid = password.length === 0 || password.length >= 6;
  const dirty =
    displayName.trim() !== target.displayName ||
    uname !== target.username ||
    (canChangeRole && role !== target.role) ||
    password.length > 0;

  async function save() {
    if (busy || !dirty || !unameValid || !passValid) return;
    setBusy(true);
    setErr("");
    const patch: { displayName?: string; username?: string; role?: "host" | "admin"; password?: string } = {};
    if (displayName.trim() && displayName.trim() !== target.displayName) patch.displayName = displayName.trim();
    if (uname !== target.username) patch.username = uname;
    if (canChangeRole && role !== target.role) patch.role = role;
    if (password.length > 0) patch.password = password;
    try {
      const r = await api.updateUser(target.id, patch);
      onSaved(r.user);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Could not save changes");
      setBusy(false);
    }
  }

  const roleNote = isSelf
    ? "you can't change your own role"
    : target.role === "super_admin"
      ? "the super admin role is locked"
      : "only the super admin can change roles";

  return (
    <AtlModalOverlay onClose={busy ? undefined : onClose}>
      <AtlModalShell
        icon={<Ico name="team" size={20} />}
        iconBg="var(--atl-gold-soft)"
        iconFg="var(--atl-gold)"
        title="Edit account"
        sub={`@${target.username}${isSelf ? " · you" : ""}`}
        onClose={onClose}
        max={460}
      >
        <div style={{ display: "grid", gap: 14, marginTop: 16 }}>
          <div>
            <div className="atl-eyebrow" style={{ marginBottom: 8 }}>NAME</div>
            <input className="atl-tf" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Sam B." />
          </div>
          <div>
            <div className="atl-eyebrow" style={{ marginBottom: 8 }}>USERNAME</div>
            <input className="atl-tf" value={username} onChange={(e) => setUsername(e.target.value.replace(/\s/g, ""))} placeholder="sam" />
            {!unameValid && uname.length > 0 && (
              <p style={{ fontSize: 12, color: "var(--atl-accent)", fontWeight: 600, marginTop: 6 }}>3–32 characters: letters, numbers, . _ -</p>
            )}
          </div>
          <div>
            <div className="atl-eyebrow" style={{ marginBottom: 8 }}>ROLE</div>
            {canChangeRole ? (
              <select className="atl-select" style={{ width: "100%" }} value={role} onChange={(e) => setRole(e.target.value as "host" | "admin")}>
                <option value="host">Host</option>
                <option value="admin">Admin</option>
              </select>
            ) : (
              <p style={{ fontSize: 13.5, color: "var(--atl-soft)", fontWeight: 600 }}>
                {target.role === "super_admin" ? "Super admin" : target.role === "admin" ? "Admin" : "Host"}
                <span style={{ color: "var(--atl-faint)", fontWeight: 500 }}> · {roleNote}</span>
              </p>
            )}
          </div>
          <div>
            <div className="atl-eyebrow" style={{ marginBottom: 8 }}>NEW PASSWORD</div>
            <input className="atl-tf" type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Leave blank to keep current" />
            {!passValid && (
              <p style={{ fontSize: 12, color: "var(--atl-accent)", fontWeight: 600, marginTop: 6 }}>At least 6 characters</p>
            )}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 20 }}>
          <button className="atl-btn atl-btn--ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            className="atl-btn atl-btn--primary"
            style={{ marginLeft: "auto" }}
            disabled={busy || !dirty || !unameValid || !passValid}
            onClick={save}
          >
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
        {err && (
          <div style={{ marginTop: 12 }}>
            <span className="atl-chip" style={{ fontWeight: 600, borderColor: "#e2c5ca", color: "var(--atl-accent)", background: "#fbf2f3" }}>
              {err}
            </span>
          </div>
        )}
      </AtlModalShell>
    </AtlModalOverlay>
  );
}
